import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MarkdownEditor } from '@/components/MarkdownEditor';
import { FileMultiSelect } from '@/components/FileMultiSelect';
import { useAppStore } from '@/stores/appStore';
import { cn } from '@/lib/utils';
import type { Action, CreateActionRequest, UpdateActionRequest } from '@/types';

interface ActionFormProps {
  action?: Action; // For editing existing actions
  onSubmit: (data: CreateActionRequest | UpdateActionRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  className?: string;
}

interface FormData {
  actionName: string;
  prompt: string;
  fileName: string | string[];
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  additionalRecipients: string[];
  isActive: boolean;
}

interface FormErrors {
  actionName?: string;
  prompt?: string;
  fileName?: string;
  frequency?: string;
  additionalRecipients?: string;
}

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
] as const;

export function ActionForm({
  action,
  onSubmit,
  onCancel,
  loading = false,
  className,
}: ActionFormProps) {
  const { actions } = useAppStore();
  const { corpusFiles, corpusFilesLoading } = actions;

  // Initialize form data
  const [formData, setFormData] = useState<FormData>({
    actionName: action?.actionDetail.actionName || '',
    prompt: action?.actionDetail.prompt || '',
    fileName: action?.actionDetail.fileReference.fileName || [],
    frequency: action?.frequency || 'daily',
    additionalRecipients: action?.actionDetail.additionalRecipients || [],
    isActive: action?.isActive ?? true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<keyof FormData, boolean>>({
    actionName: false,
    prompt: false,
    fileName: false,
    frequency: false,
    additionalRecipients: false,
    isActive: false,
  });

  // Load corpus files when component mounts
  useEffect(() => {
    if (!corpusFilesLoading && corpusFiles.length === 0) {
      useAppStore.getState().fetchCorpusFiles();
    }
  }, [corpusFiles.length, corpusFilesLoading]);

  // Validation functions
  const validateActionName = (value: string): string | undefined => {
    if (!value.trim()) {
      return 'Action name is required';
    }
    if (value.trim().length < 3) {
      return 'Action name must be at least 3 characters long';
    }
    if (value.length > 100) {
      return 'Action name must be less than 100 characters';
    }
    return undefined;
  };

  const validatePrompt = (value: string): string | undefined => {
    if (!value.trim()) {
      return 'Prompt is required';
    }
    if (value.trim().length < 10) {
      return 'Prompt must be at least 10 characters long';
    }
    if (value.length > 2000) {
      return 'Prompt must be less than 2000 characters';
    }
    return undefined;
  };

  const validateFileName = (value: string | string[]): string | undefined => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return 'At least one file must be selected';
      }
    } else if (!value || !value.trim()) {
      return 'File selection is required';
    }
    return undefined;
  };

  const validateAdditionalRecipients = (value: string[]): string | undefined => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    for (const email of value) {
      if (!emailRegex.test(email.trim())) {
        return `Invalid email address: ${email}`;
      }
    }
    return undefined;
  };

  // Validate all fields
  const validateForm = (): FormErrors => {
    return {
      actionName: validateActionName(formData.actionName),
      prompt: validatePrompt(formData.prompt),
      fileName: validateFileName(formData.fileName),
      additionalRecipients: validateAdditionalRecipients(formData.additionalRecipients),
    };
  };

  // Handle field changes
  const handleFieldChange = <K extends keyof FormData>(
    field: K,
    value: FormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Validate field if it's been touched
    if (touched[field]) {
      const fieldErrors = { ...errors };
      
      switch (field) {
        case 'actionName':
          fieldErrors.actionName = validateActionName(value as string);
          break;
        case 'prompt':
          fieldErrors.prompt = validatePrompt(value as string);
          break;
        case 'fileName':
          fieldErrors.fileName = validateFileName(value as string | string[]);
          break;
        case 'additionalRecipients':
          fieldErrors.additionalRecipients = validateAdditionalRecipients(value as string[]);
          break;
      }
      
      setErrors(fieldErrors);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const newTouched = {
      actionName: true,
      prompt: true,
      fileName: true,
      frequency: true,
      additionalRecipients: true,
      isActive: true,
    };
    setTouched(newTouched);
    
    // Validate all fields
    const formErrors = validateForm();
    setErrors(formErrors);
    
    // Check if there are any errors
    const hasErrors = Object.values(formErrors).some(error => error !== undefined);
    if (hasErrors) {
      return;
    }
    
    try {
      if (action) {
        // Update existing action
        const updateData: UpdateActionRequest = {
          actionName: formData.actionName.trim(),
          prompt: formData.prompt.trim(),
          fileName: Array.isArray(formData.fileName) 
            ? formData.fileName 
            : formData.fileName.trim(),
          frequency: formData.frequency,
          additionalRecipients: formData.additionalRecipients,
          isActive: formData.isActive,
        };
        await onSubmit(updateData);
      } else {
        // Create new action
        const createData: CreateActionRequest = {
          actionName: formData.actionName.trim(),
          prompt: formData.prompt.trim(),
          fileName: Array.isArray(formData.fileName) 
            ? formData.fileName 
            : formData.fileName.trim(),
          frequency: formData.frequency,
          additionalRecipients: formData.additionalRecipients,
          isActive: formData.isActive,
        };
        await onSubmit(createData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      // Error handling is done by the parent component
    }
  };

  const isFormValid = () => {
    const formErrors = validateForm();
    return !Object.values(formErrors).some(error => error !== undefined);
  };

  return (
    <form onSubmit={handleSubmit} className={cn("space-y-6", className)}>
      {/* Action Name Field */}
      <div className="space-y-2">
        <Label htmlFor="actionName" className="text-sm font-medium">
          Action Name *
        </Label>
        <input
          id="actionName"
          type="text"
          value={formData.actionName}
          onChange={(e) => handleFieldChange('actionName', e.target.value)}
          placeholder="Enter a name for this action..."
          disabled={loading}
          className={cn(
            "w-full px-3 py-2 border rounded-md",
            "focus:outline-none focus:ring-2 focus:ring-primary",
            touched.actionName && errors.actionName && "border-destructive"
          )}
        />
        {touched.actionName && errors.actionName && (
          <p className="text-sm text-destructive">{errors.actionName}</p>
        )}
        <p className="text-xs text-muted-foreground">
          A descriptive name to identify this action.
        </p>
      </div>

      {/* Prompt Field */}
      <div className="space-y-2">
        <Label htmlFor="prompt" className="text-sm font-medium">
          Prompt *
        </Label>
        <MarkdownEditor
          value={formData.prompt}
          onChange={(value) => handleFieldChange('prompt', value)}
          placeholder="Enter your action prompt in markdown format..."
          disabled={loading}
          error={touched.prompt && !!errors.prompt}
        />
        {touched.prompt && errors.prompt && (
          <p className="text-sm text-destructive">{errors.prompt}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Describe what you want the AI to do with the selected file. You can use markdown formatting.
        </p>
      </div>

      {/* File Selection */}
      <div className="space-y-2">
        <Label htmlFor="fileName" className="text-sm font-medium">
          Warrant Files *
        </Label>
        <FileMultiSelect
          files={corpusFiles}
          selectedFiles={Array.isArray(formData.fileName) ? formData.fileName : formData.fileName ? [formData.fileName] : []}
          onChange={(files) => handleFieldChange('fileName', files)}
          disabled={loading || corpusFilesLoading}
          error={touched.fileName && !!errors.fileName}
          placeholder={
            corpusFilesLoading 
              ? "Loading files..." 
              : corpusFiles.length === 0 
                ? "No files available" 
                : "Select warrant files"
          }
        />
        {touched.fileName && errors.fileName && (
          <p className="text-sm text-destructive">{errors.fileName}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Select one or more warrant files that this action will process. Use "Select All" to choose all files.
        </p>
      </div>

      {/* Frequency Selection */}
      <div className="space-y-2">
        <Label htmlFor="frequency" className="text-sm font-medium">
          Frequency *
        </Label>
        <Select
          value={formData.frequency}
          onValueChange={(value: 'daily' | 'weekly' | 'monthly' | 'yearly') => 
            handleFieldChange('frequency', value)
          }
          disabled={loading}
        >
          <SelectTrigger id="frequency" aria-label="Frequency">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FREQUENCY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          How often should this action be executed automatically.
        </p>
      </div>

      {/* Additional Recipients Field */}
      <div className="space-y-2">
        <Label htmlFor="additionalRecipients" className="text-sm font-medium">
          Additional Recipients
        </Label>
        <input
          id="additionalRecipients"
          type="text"
          value={formData.additionalRecipients.join(', ')}
          onChange={(e) => {
            const emails = e.target.value.split(',').map(email => email.trim()).filter(email => email);
            handleFieldChange('additionalRecipients', emails);
          }}
          placeholder="email1@example.com, email2@example.com"
          disabled={loading}
          className={cn(
            "w-full px-3 py-2 border rounded-md",
            "focus:outline-none focus:ring-2 focus:ring-primary",
            touched.additionalRecipients && errors.additionalRecipients && "border-destructive"
          )}
        />
        {touched.additionalRecipients && errors.additionalRecipients && (
          <p className="text-sm text-destructive">{errors.additionalRecipients}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Optional: Enter additional email addresses separated by commas to receive action results.
        </p>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading || !isFormValid()}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {action ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            action ? 'Update Action' : 'Create Action'
          )}
        </Button>
      </div>
    </form>
  );
}